/**
 * Les sections de « Mon compte », et la lecture de `?tab=`.
 *
 * Les quatre écrans du profil (profil, préférences, notifications,
 * communication) ont été quatre routes, puis quatre onglets. Ce sont
 * maintenant quatre sections d'une même page ; `?tab=` (ou un `#fragment`)
 * fait défiler jusqu'à la bonne. Les noms `ProfileTab` / `PROFILE_TABS` restent
 * ceux des liens déjà partis.
 *
 * Pur TS, sans Angular : la table des onglets et la tolérance aux valeurs
 * héritées se testent sans TestBed.
 */

/** Un onglet de l'écran « Mon compte ». */
export type ProfileTab = 'profile' | 'preferences' | 'notifications' | 'communication';

/** L'ordre à l'écran ; l'index dans ce tableau EST l'index du `mat-tab-group`. */
export const PROFILE_TABS: readonly ProfileTab[] = [
  'profile',
  'preferences',
  'notifications',
  'communication'
];

/** L'onglet affiché quand `?tab=` est absent ou illisible. */
export const DEFAULT_PROFILE_TAB: ProfileTab = 'profile';

/**
 * Valeurs héritées qu'il faut continuer d'accepter.
 *
 * `settings` était l'onglet « réglages » de l'ancienne page Notifications
 * (`/apps/user-profile/notifications?tab=settings`) — c'est exactement ce que
 * montre désormais l'onglet `notifications`. `list` y désignait la liste des
 * notifications, partie sur `/apps/notifications` : on ouvre alors l'onglet des
 * réglages plutôt que de faire semblant que la liste est là.
 */
const LEGACY_TAB_ALIASES: Readonly<Record<string, ProfileTab>> = {
  settings: 'notifications',
  list: 'notifications'
};

/** Libellé i18n d'un onglet. */
export function profileTabLabelKey(tab: ProfileTab): string {
  return `shared.contextual.userProfile.${tab}`;
}

/** Icône d'un onglet (ligature Material, comme le reste de l'écran). */
export function profileTabIcon(tab: ProfileTab): string {
  switch (tab) {
    case 'profile': return 'person';
    case 'preferences': return 'tune';
    case 'notifications': return 'notifications';
    case 'communication': return 'forward_to_inbox';
  }
}

/**
 * Lire `?tab=` : un onglet connu, un alias hérité, ou l'onglet par défaut.
 *
 * Ne lève jamais — une URL bricolée à la main ouvre le profil plutôt qu'un
 * écran vide.
 */
export function resolveProfileTab(raw: string | null | undefined): ProfileTab {
  if (!raw) return DEFAULT_PROFILE_TAB;
  const value = raw.trim().toLowerCase();
  if ((PROFILE_TABS as readonly string[]).includes(value)) return value as ProfileTab;
  return LEGACY_TAB_ALIASES[value] ?? DEFAULT_PROFILE_TAB;
}

/**
 * Clé i18n de la description d'une section, quand l'écran en a déjà une.
 * `null` : la section n'affiche que son titre.
 */
export function profileSectionDescriptionKey(tab: ProfileTab): string | null {
  switch (tab) {
    case 'preferences': return 'userProfile.preferences.subtitle';
    case 'communication': return 'userProfile.communication.description';
    default: return null;
  }
}

/**
 * La section vers laquelle faire défiler à l'ouverture : `?tab=` (liens
 * existants, alias hérités compris) l'emporte sur un `#fragment`. `null` quand
 * aucun des deux n'est là — la page s'ouvre en haut.
 */
export function profileSectionTarget(
  rawTab: string | null | undefined,
  fragment: string | null | undefined
): ProfileTab | null {
  if (rawTab) return resolveProfileTab(rawTab);
  const value = fragment?.trim().toLowerCase();
  return (PROFILE_TABS as readonly string[]).includes(value ?? '') ? (value as ProfileTab) : null;
}

/** Index du `mat-tab-group` pour une valeur de `?tab=`. */
export function profileTabIndex(raw: string | null | undefined): number {
  return PROFILE_TABS.indexOf(resolveProfileTab(raw));
}

/** L'onglet correspondant à un index, borné sur la table. */
export function profileTabAt(index: number): ProfileTab {
  return PROFILE_TABS[index] ?? DEFAULT_PROFILE_TAB;
}
